import React, { useState, useEffect, createContext, useContext } from 'react';
import { useAuth } from 'react-oidc-context';
import { orgPropAPI } from '../utils/apiClient';
import { Spinner, Container } from 'react-bootstrap';
import OrgSelectorModal from './OrgSelectorModal';
import CurrentOrgIndicator from './CurrentOrgIndicator';

// Create Auth Context
const AuthContext = createContext();

// Hook to use auth context
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthWrapper');
  }
  return context;
};

/**
 * AuthWrapper - Handles role detection and org selection
 * Wraps the entire app to provide auth context
 */
const AuthWrapper = ({ children }) => {
  const auth = useAuth();
  
  const [authData, setAuthData] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user role and org on mount
  useEffect(() => {
    const fetchAuthData = async () => {
      const token = auth.user?.access_token;
      const userId = auth.user?.profile?.sub;

      if (!token || !userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Call the new auth endpoint
        const response = await orgPropAPI.getAuthRole(token, userId);
        console.log('Auth data received:', response);

        setAuthData(response);

        // If superadmin, show org selector
        if (response.isSuperadmin) {
          setShowOrgSelector(true);
        } else {
          // Regular user or admin - use their assigned org
          setSelectedOrg({
            hash: response.o,
            name: null, // We don't have the name yet, could fetch from Customers table
          });
        }

      } catch (err) {
        console.error('Error fetching auth data:', err);
        setError('Failed to load user permissions');
      } finally {
        setLoading(false);
      }
    };

    if (auth.isAuthenticated) {
      fetchAuthData();
    }
  }, [auth.isAuthenticated, auth.user]);

  // Handle org selection for superadmin
  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
    setShowOrgSelector(false);
  };

  // Handle org switch (for superadmin)
  const handleOrgSwitch = () => {
    setShowOrgSelector(true);
  };

  // Show loading spinner
  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading permissions...</p>
      </Container>
    );
  }

  // Show error
  if (error) {
    return (
      <Container className="text-center mt-5">
        <div className="alert alert-danger">{error}</div>
      </Container>
    );
  }

  // Context value to provide to children
  const contextValue = {
    role: authData?.role,
    isSuperadmin: authData?.isSuperadmin || false,
    isAdmin: authData?.role === 'admin',
    isUser: authData?.role === 'user',
    orgHash: selectedOrg?.hash,
    orgName: selectedOrg?.name,
    availableOrgs: authData?.availableOrgs || [],
    switchOrg: handleOrgSwitch,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {/* Org Selector Modal for Superadmin */}
      {authData?.isSuperadmin && (
        <>
          <OrgSelectorModal
            show={showOrgSelector}
            organizations={authData.availableOrgs}
            onSelect={handleOrgSelect}
          />
          
          {/* Show current org indicator when org is selected */}
          {selectedOrg && !showOrgSelector && (
            <CurrentOrgIndicator
              orgName={selectedOrg.name}
              onSwitch={handleOrgSwitch}
            />
          )}
        </>
      )}

      {/* Pass authData and selectedOrg as props to children */}
      {(selectedOrg || !authData?.isSuperadmin) && 
        React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { authData, selectedOrg });
          }
          return child;
        })
      }
    </AuthContext.Provider>
  );
};

export default AuthWrapper;